import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import ThemeToggleButton from "../helper/ThemeToggleButton";

const MasterLayout = ({ children }) => {
  let [sidebarActive, seSidebarActive] = useState(false);
  let [mobileMenu, setMobileMenu] = useState(false);
  const location = useLocation(); // Hook to get the current route
  const navigate = useNavigate(); // Hook for navigation
  const [user, setUser] = useState({ fullname: '', role: '' });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Check authentication on component mount and whenever route changes
  useEffect(() => {
    const checkAuthentication = () => {
      const token = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');

      // If no token or user data, redirect to login
      if (!token || !storedUser) {
        console.warn('No authentication found. Redirecting to login...');
        localStorage.clear(); // Clear any remaining data
        navigate('/sign-in', { replace: true });
        setIsCheckingAuth(false);
        return;
      }

      try {
        // Validate user data
        const userData = JSON.parse(storedUser);
        
        if (!userData.id || !userData.username) {
          throw new Error('Invalid user data');
        }

        // Set user data
        const userRole = (userData.role || 'user').toLowerCase(); // Normalize to lowercase
        setUser({
          fullname: userData.fullname || 'User',
          role: userRole
        });
        
        // All routes now require authentication (handled by token check above)
        // No admin-only routes restriction needed
        
        setIsAuthenticated(true);
        setIsCheckingAuth(false);
      } catch (error) {
        console.error('Error validating authentication:', error);
        localStorage.clear();
        navigate('/sign-in', { replace: true });
        setIsCheckingAuth(false);
      }
    };

    checkAuthentication();
  }, [navigate, location.pathname]);

  // Toggle dropdown function
  const toggleDropdown = (dropdownName) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  let sidebarControl = () => {
    seSidebarActive(!sidebarActive);
  };

  let mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  // Logout handler
  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      // Call logout API to invalidate token on server
      if (token) {
        try {
          await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });
        } catch (apiError) {
          console.error('API logout error:', apiError);
          // Continue with local logout even if API call fails
        }
      }
      
      // Clear all authentication data from localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('role');
      
      // Redirect to login page
      navigate('/sign-in');
    } catch (error) {
      console.error('Error during logout:', error);
      // Still redirect even if there's an error
      navigate('/sign-in');
    }
  };

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary-600" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-secondary-light">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Don't render the layout if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <section className={mobileMenu ? "overlay active" : "overlay "}>
      {/* sidebar */}
      <aside
        className={
          sidebarActive
            ? "sidebar active "
            : mobileMenu
            ? "sidebar sidebar-open"
            : "sidebar"
        }
      >
        <button
          onClick={mobileMenuControl}
          type='button'
          className='sidebar-close-btn'
        >
          <Icon icon='radix-icons:cross-2' />
        </button>
        <div>
          <Link to='/' className='sidebar-logo'>
            <img
              src='assets/images/sidebarlogo.png'
              alt='site logo'
              className='light-logo'
            />
            {/* <img
              src='assets/images/sidebarlogo.png'
              alt='site logo'
              className='dark-logo'
            />
            <img
              src='assets/images/sidebarlogo.png'
              alt='site logo'
              className='logo-icon'
            /> */}
          </Link>
        </div>
        <div className='sidebar-menu-area'>
          <ul className='sidebar-menu' id='sidebar-menu'>
             {/* Users Dropdown */}
             <li className={`dropdown ${openDropdown === 'users' ? 'open' : ''}`}>
              <a 
                href='#' 
                onClick={(e) => { 
                  e.preventDefault(); 
                  toggleDropdown('users'); 
                }}
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                <Icon
                  icon='flowbite:users-group-outline'
                  className='menu-icon'
                />
                <span style={{ flex: 1, opacity: 1, visibility: 'visible' }}>Users</span>
              </a>
              <ul 
                className='sidebar-submenu' 
                style={{ 
                  display: openDropdown === 'users' ? 'block' : 'none',
                  maxHeight: openDropdown === 'users' ? '500px' : '0',
                  overflow: openDropdown === 'users' ? 'visible' : 'hidden',
                  transition: 'all 0.3s ease'
                }}
              >
                <li>
                  <NavLink
                    to='/users-list'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                    onClick={() => setOpenDropdown(null)}
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />{" "}
                    Users List
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/add-user'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                    onClick={() => setOpenDropdown(null)}
                  >
                    <i className='ri-circle-fill circle-icon text-info-main w-auto' />{" "}
                    Add User
                  </NavLink>
                </li>
              </ul>
            </li>

            {/* Employees Dropdown */}
            <li className={`dropdown ${openDropdown === 'employees' ? 'open' : ''}`}>
              <a 
                href='#' 
                onClick={(e) => { 
                  e.preventDefault(); 
                  toggleDropdown('employees'); 
                }}
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                <Icon
                  icon='mdi:badge-account-outline'
                  className='menu-icon'
                />
                <span style={{ flex: 1, opacity: 1, visibility: 'visible' }}>Employees</span>
              </a>
              <ul 
                className='sidebar-submenu' 
                style={{ 
                  display: openDropdown === 'employees' ? 'block' : 'none',
                  maxHeight: openDropdown === 'employees' ? '500px' : '0',
                  overflow: openDropdown === 'employees' ? 'visible' : 'hidden',
                  transition: 'all 0.3s ease'
                }}
              >
                <li>
                  <NavLink
                    to='/employee-list'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                    onClick={() => setOpenDropdown(null)}
                  >
                    <i className='ri-circle-fill circle-icon text-primary-600 w-auto' />{" "}
                    Employee List
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to='/add-employee'
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                    onClick={() => setOpenDropdown(null)}
                  >
                    <i className='ri-circle-fill circle-icon text-success-main w-auto' />{" "}
                    Register Employee
                  </NavLink>
                </li>
              </ul>
            </li>

          </ul>
        </div>
      </aside>

      <main
        className={sidebarActive ? "dashboard-main active" : "dashboard-main"}
      >
        <div className='navbar-header'>
          <div className='row align-items-center justify-content-between'>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-4'>
                <button
                  type='button'
                  className='sidebar-toggle'
                  onClick={sidebarControl}
                >
                  {sidebarActive ? (
                    <Icon
                      icon='iconoir:arrow-right'
                      className='icon text-2xl non-active'
                    />
                  ) : (
                    <Icon
                      icon='heroicons:bars-3-solid'
                      className='icon text-2xl non-active '
                    />
                  )}
                </button>
                <button
                  onClick={mobileMenuControl}
                  type='button'
                  className='sidebar-mobile-toggle'
                >
                  <Icon icon='heroicons:bars-3-solid' className='icon' />
                </button>
                {/* <form className='navbar-search'>
                  <input type='text' name='search' placeholder='Search' />
                  <Icon icon='ion:search-outline' className='icon' />
                </form> */}
              </div>
            </div>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-3'>
                {/* ThemeToggleButton */}
                {/* <ThemeToggleButton /> */}
               
                
            
                <div className='dropdown'>
                  <button
                    className='d-flex justify-content-center align-items-center rounded-circle'
                    type='button'
                    data-bs-toggle='dropdown'
                  >
                    <img
                      src='assets/images/user.jpg'
                      alt='image_user'
                      className='w-40-px h-40-px object-fit-cover rounded-circle'
                    />
                  </button>
                  <div className='dropdown-menu to-top dropdown-menu-sm'>
                    <div className='py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2'>
                      <div>
                        <h6 className='text-lg text-primary-light fw-semibold mb-2'>
                          {user.fullname || 'User'}
                        </h6>
                        <span className='text-secondary-light fw-medium text-sm'>
                          {user.role || 'User'}
                        </span>
                      </div>
                      <button type='button' className='hover-text-danger'>
                        <Icon
                          icon='radix-icons:cross-1'
                          className='icon text-xl'
                        />
                      </button>
                    </div>
                    <ul className='to-top-list'>
                      <li>
                        <button
                          className='dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3'
                          onClick={handleLogout}
                          style={{ border: 'none', background: 'transparent', width: '100%', cursor: 'pointer' }}
                        >
                          <Icon icon='lucide:power' className='icon text-xl' />{" "}
                          Log Out
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
                {/* Profile dropdown end */}
              </div>
            </div>
          </div>
        </div>

        {/* dashboard-main-body */}
        <div className='dashboard-main-body'>{children}</div>

        {/* Footer section */}
        <footer className='d-footer'>
          <div className='row align-items-center'>
            <div className='col-auto'>
              <p className='mb-0'>© {new Date().getFullYear()} SDRB IT Department. All Rights Reserved.</p>
            </div>
          </div>
        </footer>
      </main>
    </section>
  );
};

export default MasterLayout;
